import React, { useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import {
  LuCalendar,
  LuCreditCard,
  LuDatabase,
  LuFileText,
  LuPencil,
  LuReceipt,
  LuTrash2,
  LuTrendingUp,
  LuWallet,
} from "react-icons/lu";

import Button from "../ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardClass,
  cardContentFlushClass,
  creditCellClass,
  creditTextClass,
  debitCellClass,
  debitTextClass,
  mobileCardClass,
  moneyCellClass,
  tableHeadCellClass,
} from "../ui/styles";
import useConfirmPopup from "../../context/useConfirmPopup";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  setEditSalary,
  toggleCreateModal,
} from "../../features/salaries/salariesSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { GET } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { toastStyles } from "../../static/styles";
import { SalariesProps } from "../../types/salaries";

const formatAmount = (value: number | null | undefined) =>
  (value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value: string) => moment(value).format("DD-MM-YYYY");

const formatDateTime = (value: string) =>
  moment(value).format("DD-MM-YYYY hh:mm A");

const isCredit = (salary: SalariesProps) =>
  salary.status === 0 || salary.status === 1;

const noteOf = (salary: SalariesProps) =>
  salary.status !== 0 && salary.note ? String(salary.note) : "";

const canEdit = (salary: SalariesProps) =>
  !!salary.isEditable && salary.status !== 1;

const actionCount = (
  salary: SalariesProps,
  canReport: boolean,
  canUpdate: boolean,
  canDelete: boolean
) =>
  (salary.status === 0 && canReport ? 1 : 0) +
  (canEdit(salary) && canUpdate ? 1 : 0) +
  (salary.isEditable && canDelete ? 1 : 0);

const actionSpinnerClass = cx(
  buttonSpinnerClass,
  "mr-0 border-[#113F67]/30 border-t-[#113F67]"
);

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const noteLineClass = "flex items-start gap-1.5 text-sm text-gray-600";

const typeHead = (salary: SalariesProps) => {
  if (salary.status === 0) {
    return (
      <div className="flex items-center gap-1.5">
        <LuReceipt className="h-3.5 w-3.5 text-blue-600" />
        <span className="text-sm font-semibold text-blue-900">હિસાબ</span>
      </div>
    );
  }

  if (salary.status === 1) {
    return (
      <div className="flex items-center gap-1.5">
        <LuTrendingUp className="h-3.5 w-3.5 text-green-600" />
        <span className="text-sm font-semibold text-green-900">બોનસ</span>
      </div>
    );
  }

  if (salary.status === 2) {
    return (
      <div className="flex items-center gap-1.5">
        <LuWallet className="h-3.5 w-3.5 text-orange-600" />
        <span className="text-sm font-semibold text-orange-900">ઉપાડ</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <LuCreditCard className="h-3.5 w-3.5 text-green-600" />
      <span className="text-sm font-semibold text-green-900">પગાર</span>
    </div>
  );
};

const typeDetails = (salary: SalariesProps) => {
  if (salary.status === 0) {
    return (
      <div className="text-sm text-gray-600">
        {formatDate(salary.from_date)} to {formatDate(salary.to_date)}
        <span className="block">
          કલાક × રેટ : {salary.note?.hours ?? 0} × {salary.note?.amount ?? 0}
        </span>
        <span className="block">
          રાઉન્ડ ઑફ : {formatAmount(salary.roundoff_amount)}
        </span>
        <span className="block">
          ઉપાડ બાદ : {formatAmount(salary.withdrawal_deposit)}
        </span>
        <span className="block">
          અગાઉ ના બાકી : {formatAmount(salary.remaining_payment)}
        </span>
      </div>
    );
  }

  return null;
};

type Props = {
  salaries: SalariesProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  handleDeleteSalaries: (id: number) => void;
};

const SalariesList = ({
  salaries,
  pageNumber,
  pageSize,
  isLoading,
  handleDeleteSalaries,
}: Props) => {
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmPopup();
  const loggingUser = useAppSelector(getLoggingUser);

  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.salariesReport
  );
  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.salariesUpdate
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.salariesDelete
  );

  const permittedActionCount =
    (canReport ? 1 : 0) + (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);
  const tableColSpan = 8 + (permittedActionCount > 0 ? 1 : 0);

  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  const handleEditSalary = async (salary: SalariesProps) => {
    if (salary.status === 0) {
      await dispatch(setEditSalary(salary));
      dispatch(toggleCreateModal(1));
    } else if (salary.status === 2) {
      await dispatch(setEditSalary(salary));
      dispatch(toggleCreateModal(2));
    } else if (salary.status === 3) {
      await dispatch(setEditSalary(salary));
      dispatch(toggleCreateModal(3));
    }
  };

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (confirmed) {
      setIsActionLoading(id);
      await handleDeleteSalaries(id);
      setIsActionLoading(0);
    }
  };

  const handlePrintReport = async (id: number) => {
    try {
      setIsActionLoading(id);
      const response: any = await GET(`/v1/admin/salaries/${id}/reports`, {});

      if (response.data.status === "success") {
        dispatch(updatePdfFile(response.data.data));
      } else {
        toast.error(response.data.message, {
          ...(toastStyles as any),
        });
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
        {
          ...(toastStyles as any),
        }
      );
    } finally {
      setIsActionLoading(0);
    }
  };

  return (
    <>
      <div className={cx(cardClass, "max-sm:hidden")}>
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={cx(tableHeadCellClass, "text-center")}>
                    ક્રમ
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-center")}>
                    એકાઉન્ટ ID
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-center")}>
                    પ્રકાર
                  </TableHead>
                  <TableHead className={tableHeadCellClass}>વિગતો</TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    જમા
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    ઉધાર
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    બેલેન્સ
                  </TableHead>
                  <TableHead className={tableHeadCellClass}>તારીખ</TableHead>
                  {permittedActionCount > 0 && (
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      એકશન્સ બટન
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={tableColSpan} />
              ) : salaries.length === 0 ? (
                <TableEmptyRow
                  colSpan={tableColSpan}
                  label="પગાર ની વિગતો મળી નથી"
                />
              ) : (
                <TableBody>
                  {salaries.map((salary, index) => (
                    <TableRow key={salary.id}>
                      <TableCell className="font-medium text-center">
                        {(pageNumber - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell className="font-medium text-center">
                        {salary.id}
                      </TableCell>

                      <TableCell className="text-center">
                        {isCredit(salary) ? (
                          <p className={creditTextClass}>જમા</p>
                        ) : (
                          <p className={debitTextClass}>ઉધાર</p>
                        )}
                      </TableCell>

                      <TableCell>
                        <div className="min-w-0 space-y-1.5">
                          {typeHead(salary)}
                          {typeDetails(salary)}
                          {noteOf(salary) && (
                            <div className={noteLineClass}>
                              <LuFileText className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                              <span className="break-words">
                                {noteOf(salary)}
                              </span>
                            </div>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className={creditCellClass}>
                        {formatAmount(salary.credit_amount)}
                      </TableCell>

                      <TableCell className={debitCellClass}>
                        {formatAmount(salary.debit_amount)}
                      </TableCell>

                      <TableCell className={moneyCellClass}>
                        {formatAmount(salary.balance)}
                      </TableCell>

                      <TableCell className="text-sm text-gray-500">
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1.5">
                            <LuCalendar className="h-3.5 w-3.5 text-blue-800" />
                            {formatDate(salary.date || salary.created_at)}
                          </span>
                          <span className="flex items-center gap-1.5 text-xs text-gray-400">
                            <LuDatabase className="h-3.5 w-3.5 text-gray-400" />
                            {formatDateTime(salary.created_at)}
                          </span>
                        </div>
                      </TableCell>

                      {permittedActionCount > 0 && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isActionLoading === salary.id ? (
                              <div className={actionSpinnerClass}></div>
                            ) : (
                              <>
                                {salary.status === 0 && canReport && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="રિપોર્ટ"
                                    className="text-green-600 hover:text-green-700"
                                    onClick={() => handlePrintReport(salary.id)}
                                  >
                                    <LuFileText className="h-4 w-4" />
                                  </Button>
                                )}

                                {canEdit(salary) && canUpdate && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="સંપાદિત કરો"
                                    onClick={() => handleEditSalary(salary)}
                                  >
                                    <LuPencil className="h-4 w-4" />
                                  </Button>
                                )}

                                {salary.isEditable && canDelete && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="કાઢી નાખો"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => handleDeleteRow(salary.id)}
                                  >
                                    <LuTrash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : salaries.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label="પગાર ની વિગતો મળી નથી" />
          </div>
        ) : (
          salaries.map((salary, index) => (
            <div key={salary.id} className={mobileCardClass}>
              <div className="flex items-start justify-between gap-3 border-b p-4">
                {typeHead(salary)}
                <span className="shrink-0 text-sm text-gray-500">
                  ID: {salary.id}
                </span>
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-semibold text-primary">
                    {(pageNumber - 1) * pageSize + index + 1}
                  </span>
                </div>

                {(typeDetails(salary) || noteOf(salary)) && (
                  <div className={cardRowClass}>
                    <span className={cardLabelClass}>વિગતો</span>
                    <span className="min-w-0 space-y-1.5 text-right">
                      {typeDetails(salary)}
                      {noteOf(salary) && (
                        <span className="block break-words text-gray-600">
                          {noteOf(salary)}
                        </span>
                      )}
                    </span>
                  </div>
                )}

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>જમા</span>
                  <span className={creditCellClass}>
                    {formatAmount(salary.credit_amount)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ઉધાર</span>
                  <span className={debitCellClass}>
                    {formatAmount(salary.debit_amount)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>બેલેન્સ</span>
                  <span className={moneyCellClass}>
                    {formatAmount(salary.balance)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDate(salary.date || salary.created_at)}
                    <span className="block text-xs text-gray-400">
                      {formatDateTime(salary.created_at)}
                    </span>
                  </span>
                </div>
              </div>

              {actionCount(salary, canReport, canUpdate, canDelete) > 0 && (
                <div
                  className={cx(
                    "grid gap-2 border-t p-4",
                    actionCount(salary, canReport, canUpdate, canDelete) === 1
                      ? "grid-cols-1"
                      : "grid-cols-2"
                  )}
                >
                  {isActionLoading === salary.id ? (
                    <div className="col-span-full flex h-10 items-center justify-center">
                      <div className={actionSpinnerClass}></div>
                    </div>
                  ) : (
                    <>
                      {salary.status === 0 && canReport && (
                        <Button
                          variant="outline"
                          title="રિપોર્ટ"
                          className="text-green-600 hover:text-green-700"
                          onClick={() => handlePrintReport(salary.id)}
                        >
                          <LuFileText className="h-4 w-4" />
                        </Button>
                      )}

                      {canEdit(salary) && canUpdate && (
                        <Button
                          variant="outline"
                          title="સંપાદિત કરો"
                          onClick={() => handleEditSalary(salary)}
                        >
                          <LuPencil className="h-4 w-4" />
                        </Button>
                      )}

                      {salary.isEditable && canDelete && (
                        <Button
                          variant="outline"
                          title="કાઢી નાખો"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteRow(salary.id)}
                        >
                          <LuTrash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};
export default SalariesList;
